import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  schema?: object;
}

export function useSEO({ title, description, keywords, schema }: SEOProps) {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    if (description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', description);
      }
    }

    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', keywords);
      }
    }

    // Add Schema.org JSON-LD
    if (schema) {
      const scriptId = 'schema-org-jsonld';
      let script = document.getElementById(scriptId) as HTMLScriptElement;
      
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      
      script.textContent = JSON.stringify(schema);
    }

    return () => {
      // Cleanup schema script on unmount
      if (schema) {
        const script = document.getElementById('schema-org-jsonld');
        if (script) {
          script.remove();
        }
      }
    };
  }, [title, description, keywords, schema]);
}

// Generate WebSite Schema
export function generateWebSiteSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': '菊田あや子 旅行会社向け送客・広報支援サービス',
    'url': siteUrl,
    'description': 'タレント菊田あや子とSNS専門チームが旅行会社のツアー集客を強力サポート。50代以上の女性層へダイレクトに届け、募集開始と同時に満席を実現。',
    'publisher': {
      '@type': 'Organization',
      'name': '菊田あや子 旅行会社向け送客・広報支援サービス',
      'url': siteUrl
    },
    'potentialAction': {
      '@type': 'SearchAction',
      'target': `${siteUrl}/?s={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };
}

// Generate Organization Schema
export function generateOrganizationSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': '菊田あや子 旅行会社向け送客・広報支援サービス',
    'url': siteUrl,
    'description': 'タレント菊田あや子とSNS専門チームによる旅行会社向けツアー送客・広報支援サービス',
    'founder': {
      '@type': 'Person',
      'name': '菊田あや子',
      'jobTitle': 'タレント・ナビゲーター'
    },
    'areaServed': {
      '@type': 'Country',
      'name': '日本'
    },
    'serviceType': [
      'ツアー送客支援',
      'SNS広報支援',
      '旅行会社向けマーケティング'
    ]
  };
}

// Generate FAQPage Schema
export function generateFAQSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': '企画がまだ漠然としていますが、相談可能ですか？',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'はい。ターゲット層に響くキーワード選定や、ツアーの切り口から一緒に考えさせていただきます。'
        }
      },
      {
        '@type': 'Question',
        'name': '地方の小規模な旅行会社でも対応いただけますか？',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'もちろんです。規模の大小にかかわらず、特定のファン層へ向けた「質の高いツアー」の実現をサポートいたします。'
        }
      },
      {
        '@type': 'Question',
        'name': '費用の目安はどのくらいですか？',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': '菊田の同行有無や、撮影・動画編集のボリュームにより異なります。ご予算に合わせて柔軟にプランニング可能です。'
        }
      },
      {
        '@type': 'Question',
        'name': '万が一、ツアーが中止になった場合の対応は？',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': '事前に取り交わす契約に基づき、広報稼働分のみを清算するなど、リスクを最小限に抑えた設計をいたします。'
        }
      }
    ]
  };
}

// Generate Service Schema
export function generateServiceSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': '旅行会社向けツアー送客・広報支援サービス',
    'provider': {
      '@type': 'Organization',
      'name': '菊田あや子 旅行会社向け送客・広報支援サービス',
      'url': siteUrl
    },
    'areaServed': {
      '@type': 'Country',
      'name': '日本'
    },
    'audience': {
      '@type': 'Audience',
      'audienceType': '旅行会社・旅行代理店'
    },
    'description': 'タレント菊田あや子とSNS専門チームが旅行会社のツアー集客を強力サポート。50代以上の女性層へダイレクトに届け、募集開始と同時に満席を実現する送客・広報支援サービス。',
    'offers': {
      '@type': 'Offer',
      'availability': 'https://schema.org/InStock'
    }
  };
}

// Generate BreadcrumbList Schema
export function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': `${siteUrl}${item.url}`
    }))
  };
}
